/** Configure durable Kuali settings; Manage awards owns every identifier batch. */
import { useEffect, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { connectorScheduleLabels } from '../../components/ConnectorScheduleSelect'
import { ConnectorQueryLockNotice } from '../../components/ConnectorQueryLockNotice'
import {
  ConnectorSourceDisplayFieldSelection,
  orderedSourceDisplayFields,
} from '../../components/ConnectorSourceDisplayFieldSelection'
import type { ConnectorFeedFor } from '../../types'
import { useKualiResearchFeedForm } from '../hooks/useKualiResearchFeedForm'
import type { KualiResearchSourceType } from '../types'
import { KualiResearchConnectionSection } from './KualiResearchConnectionSection'
import { KualiResearchDisclosure } from './KualiResearchDisclosure'
import { KualiResearchModuleTabs } from './KualiResearchModuleTabs'
import { KualiResearchSourceSelection } from './KualiResearchSourceSelection'
import { KualiResearchSyncOptions } from './KualiResearchSyncOptions'

type KualiResearchFeedDialogProps = {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId: string
  feed?: ConnectorFeedFor<'kuali-research'> | null
  // Server-derived Kuali query capability. Module and award scope edits
  // require a connector administrator grant; without it the dialog is
  // read-only so the save cannot reach a guaranteed 403.
  canEditQuery: boolean
  canManageCredentials: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigured: () => Promise<void>
}

export function KualiResearchFeedDialog(props: KualiResearchFeedDialogProps) {
  return props.open ? (
    <KualiResearchFeedEditor
      key={JSON.stringify([props.orgId, props.vaultId, props.feed?.feedId])}
      {...props}
    />
  ) : null
}

function KualiResearchFeedEditor({
  api,
  orgId,
  vaultId,
  feed: currentFeed,
  canEditQuery,
  canManageCredentials,
  open,
  onOpenChange,
  onConfigured,
}: KualiResearchFeedDialogProps) {
  // docreview-coding: Feed polling must not close sections or replace unsaved
  // edits. Each open editor owns its starting feed; closing it discards that
  // state, and reopening reads the latest feed. Permission props stay live.
  const [feed] = useState(currentFeed)

  const form = useKualiResearchFeedForm({
    api,
    orgId,
    vaultId,
    feed,
    open,
    canEditQuery,
    canAdministerWorkspace: canManageCredentials,
    onConfigured,
  })
  const [connectionOpen, setConnectionOpen] = useState(false)
  const [displayOpen, setDisplayOpen] = useState(false)
  const [activeSource, setActiveSource] = useState<KualiResearchSourceType>(
    feed?.kualiResearch?.modules[0]?.source ?? 'awards',
  )
  const [deleteCredentialOpen, setDeleteCredentialOpen] = useState(false)

  useEffect(() => {
    if (form.modules.some(module => module.source === activeSource)) return
    if (form.modules[0]) setActiveSource(form.modules[0].source)
  }, [activeSource, form.modules])

  const credentialStored = form.credentialSource === 'workspace'
    ? form.connection.workspaceConfigured === true
    : form.connection.umdConfigured === true
  const automaticModules = form.modules.filter(module => (
    module.scope === 'all' || module.scope === 'related-all-awards'
  ))
  const specificOnly = automaticModules.length === 0
  useEffect(() => {
    if (specificOnly && form.schedule !== 'manual') form.setSchedule('manual')
  }, [form, specificOnly])

  const displaySummary = form.sourceDisplayFieldKeys.length
    ? orderedSourceDisplayFields(
        form.sourceDisplayFields,
        form.sourceDisplayFieldKeys,
      )
        .map(field => field.label)
        .join(', ')
    : 'No dashboard fields'
  const invalidModule = form.modules.some(module => (
    !Number.isInteger(module.limit)
    || module.limit < 1
    || module.filters.some(filter => !filter.value.trim())
    || (module.source === 'awards' && !module.attachmentTypeCodes.length)
  ))
  const footerSummary = configurationSummary(form.modules, form.schedule)

  const changeSources = (sources: KualiResearchSourceType[]) => {
    form.setSources(sources)
    const added = sources.find(source => !form.sources.includes(source))
    if (added) setActiveSource(added)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={next => { if (!form.saving) onOpenChange(next) }}>
        <DialogContent data-testid="kuali-research-source-dialog" className="flex max-h-[92vh] flex-col gap-0 overflow-hidden rounded-[14px] p-0 shadow-[0_32px_80px_rgba(15,23,42,0.3)] sm:max-w-[600px]">
          <DialogHeader className="border-b border-slate-100 px-[22px] pb-[14px] pt-[18px] pr-12 text-left">
            <DialogTitle className="text-[15.5px] font-semibold leading-5 text-slate-950">
              Configure Kuali Research
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-[12.5px] leading-[17px] text-slate-500">
              Connection, modules, and sync behavior. Identifiers for specific pulls are entered at pull time, not here.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-[22px] py-4">
            <KualiResearchConnectionSection
              open={connectionOpen}
              onOpenChange={setConnectionOpen}
              apiUrl={form.apiUrl}
              onApiUrlChange={form.setApiUrl}
              credentialSource={form.credentialSource}
              onCredentialSourceChange={form.setCredentialSource}
              connection={form.connection}
              apiKey={form.apiKey}
              onApiKeyChange={form.setApiKey}
              saving={form.saving}
              canEditQuery={canEditQuery}
              canManageCredentials={canManageCredentials}
              onDeleteCredential={() => setDeleteCredentialOpen(true)}
            />

            <fieldset disabled={!canEditQuery} className="space-y-2.5">
              <h2 className="text-[12px] font-semibold text-slate-700">Modules & scope</h2>
              <KualiResearchSourceSelection
                selected={form.sources}
                onSelectedChange={changeSources}
              />
              <KualiResearchModuleTabs
                api={api}
                orgId={orgId}
                vaultId={vaultId}
                open={open && canEditQuery}
                apiUrl={form.apiUrl.trim()}
                credentialStored={credentialStored}
                credentialSource={form.credentialSource}
                modules={form.modules}
                activeSource={activeSource}
                onActiveSourceChange={setActiveSource}
                onModuleChange={form.updateModule}
              />
            </fieldset>

            <KualiResearchDisclosure
              title="Dashboard fields"
              summary={displaySummary}
              open={displayOpen}
              onOpenChange={setDisplayOpen}
            >
              <div className="border-t border-slate-100 px-3.5 pb-3.5 pt-3">
                <ConnectorSourceDisplayFieldSelection
                  fields={form.sourceDisplayFields}
                  selectedKeys={form.sourceDisplayFieldKeys}
                  onSelectedKeysChange={form.setSourceDisplayFieldKeys}
                  disabled={!canManageCredentials}
                />
              </div>
            </KualiResearchDisclosure>

            <KualiResearchSyncOptions
              schedule={form.schedule}
              onScheduleChange={form.setSchedule}
              specificOnly={specificOnly}
              disabled={!canManageCredentials}
            />
          </div>

          {!canEditQuery && (
            <div className="border-t border-slate-100 px-[22px] py-3">
              <ConnectorQueryLockNotice providerLabel="Kuali Research" />
            </div>
          )}

          <DialogFooter className="border-t border-slate-100 bg-slate-50/80 px-[22px] py-[13px] sm:items-center sm:justify-between">
            <p className="text-[11.5px] text-slate-500">{footerSummary}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 rounded-[8px] px-3 text-xs" disabled={form.saving} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                data-testid="kuali-research-source-save"
                size="sm"
                className="h-8 rounded-[8px] bg-slate-900 px-3.5 text-xs font-semibold hover:bg-slate-800"
                disabled={(!feed && !canEditQuery) || form.saving || form.loadingConnection || !form.apiUrl.trim() || !form.modules.length || invalidModule}
                onClick={() => void form.submit().then(saved => { if (saved) onOpenChange(false) })}
              >
                {form.saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteCredentialOpen} onOpenChange={setDeleteCredentialOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the stored Kuali Research key?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the workspace API key. Connector settings and transferred documents remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={form.saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canManageCredentials || form.saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={event => {
                event.preventDefault()
                void form.deleteCredential().then(() => setDeleteCredentialOpen(false))
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete stored key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function configurationSummary(
  modules: { scope: string; startAfter: string | null }[],
  schedule: keyof typeof connectorScheduleLabels,
): string {
  const automatic = modules.filter(module => module.scope === 'all' || module.scope === 'related-all-awards')
  if (!automatic.length) return 'Specific awards · on-demand pulls only'
  const mode = automatic.length === modules.length ? 'Automatic' : 'Mixed modes'
  const dates = automatic.map(module => module.startAfter).filter(Boolean)
  const start = dates.length ? `after ${dates[0]}` : 'all awards'
  return `${mode} · ${start} · ${connectorScheduleLabels[schedule].toLocaleLowerCase()}`
}
